import logo from "assets/images/integrations/klaviyo_logo.png";
import { useCallback, useEffect, useMemo, useState } from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import UpCustomerSettingsButtonFooter from "../../../../../components/UpCustomerSettingsButtonFooter";
import { RootReduxState } from "../../../../../utils/types";
import { selectBox as SelectBox } from "../../../../CustomerSettings/EmailSignature/input-fields";
import { ConnectedIntegrationLineWrapper } from "../../components/ConnectedIntegrationLineWrapper";
import {
  KlaviyoDataReduxState,
  KlaviyoRequestReduxState,
  deleteKlaviyoIntegrationAction,
  getKlaviyoIntegrationSettingsAction,
  resetKlaviyoIntegrationsRequestsAction,
  selectKlaviyoListAction
} from "./redux";

const mapStateToProps = (state: RootReduxState) => {
  const shopId = state.getIn(["customerBackendDashboard", "userData", "selectedShopId"]) as string;

  const selectListRequestState = state
    .getIn(["integrations", "klaviyo", "requests", "selectList"])
    ?.toJS() as KlaviyoRequestReduxState | undefined;
  const deleteRequestState = state
    .getIn(["integrations", "klaviyo", "requests", "delete"])
    ?.toJS() as KlaviyoRequestReduxState | undefined;

  return {
    shopId: Number(shopId),
    selectListRequestState: selectListRequestState ?? {
      loading: false,
      success: false,
      error: "none" as const
    },
    deleteRequestState: deleteRequestState ?? {
      loading: false,
      success: false,
      error: "none" as const
    },
    integrationData: state.getIn(["integrations", "klaviyo", "data"])?.toJS() as
      | KlaviyoDataReduxState
      | undefined
  };
};

const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getKlaviyoIntegrationSettingsAction,
      deleteKlaviyoIntegrationAction,
      resetKlaviyoIntegrationsRequestsAction,
      selectKlaviyoListAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type KlaviyoSettingsViewComponentProps = ConnectedProps<typeof connector>;

const KlaviyoSettingsViewComponent = (
  props: KlaviyoSettingsViewComponentProps & WrappedComponentProps
) => {
  const {
    integrationData,
    intl,
    selectListRequestState,
    deleteRequestState,
    deleteKlaviyoIntegrationAction,
    resetKlaviyoIntegrationsRequestsAction,
    selectKlaviyoListAction,
    getKlaviyoIntegrationSettingsAction,
    shopId
  } = props;

  const [selectedListId, setSelectedListId] = useState<string | undefined>(
    integrationData?.settings?.selectedListId
  );
  const [valueChanged, setValueChanged] = useState(false);

  useEffect(
    () => () => {
      resetKlaviyoIntegrationsRequestsAction();
    },
    []
  );

  useEffect(() => {
    if (selectListRequestState.success) {
      getKlaviyoIntegrationSettingsAction(shopId);
    }
  }, [selectListRequestState.success]);

  useEffect(() => {
    setSelectedListId(integrationData?.settings?.selectedListId);
    setValueChanged(selectedListId !== integrationData?.settings?.selectedListId);
    resetKlaviyoIntegrationsRequestsAction();
  }, [integrationData?.settings?.selectedListId]);

  useEffect(() => {
    setValueChanged(selectedListId !== integrationData?.settings?.selectedListId);
  }, [selectedListId]);

  const handleDelete = useCallback(() => {
    deleteKlaviyoIntegrationAction(shopId);
  }, [shopId]);

  const handleSubmit = useCallback(() => {
    selectKlaviyoListAction(shopId, selectedListId!);
  }, [shopId, selectedListId]);

  const handleReset = useCallback(() => {
    setSelectedListId(integrationData?.settings?.selectedListId);
  }, [integrationData]);

  const handleListChange = useCallback((value: string) => {
    setSelectedListId(value);
  }, []);

  const listsOptions = useMemo(() => {
    if (integrationData?.settings?.lists === undefined) {
      return [];
    }

    return integrationData.settings.lists.map((l) => ({ label: l.name, value: l.id }));
  }, [integrationData?.settings?.lists]);

  if (integrationData === undefined || integrationData.settings === undefined) {
    return null;
  }

  return (
    <ConnectedIntegrationLineWrapper
      accountName={"Uptain GmbH"}
      deleting={deleteRequestState.loading}
      image={logo}
      name="Klaviyo"
      onDelete={handleDelete}
      invertLogo
    >
      <div>
        <SelectBox
          input={{
            onChange: handleListChange,
            value: selectedListId,
            name: "list"
          }}
          label={intl.formatMessage({
            id: `containers.Integrations.klaviyo.settings.list.label`,
            defaultMessage: "List for Newsletter-Optins"
          })}
          placeholder="Select List"
          infoText={intl.formatMessage({
            id: `containers.Integrations.klaviyo.settings.list.info`,
            defaultMessage:
              "After successfully connected to Klaviyo new optins will be added to this list. After the creation of a new list in the connected Klaviyo Account, please, delete this configuration line and re-connect again."
          })}
          options={listsOptions}
          emptyMessage="No lists available"
        />
        <UpCustomerSettingsButtonFooter
          saveButtonDisabled={!valueChanged}
          resetButtonDisabled={!valueChanged}
          saveButtonOnClick={handleSubmit}
          resetButtonOnClick={handleReset}
          saving={selectListRequestState.loading || selectListRequestState.success}
        />
      </div>
    </ConnectedIntegrationLineWrapper>
  );
};

export const KlaviyoSettingsView = connector(injectIntl(KlaviyoSettingsViewComponent));
